<script setup>
import { ref, computed } from 'vue';
import { toast } from 'dashboard/composables/useToast';
import { useToggle, onClickOutside, useWindowSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

const store = useStore();
const { t } = useI18n();

// No celular o botao de resolver fica so com o icone: a palavra e o que
// custa mais largura na barra, e o espaco que sobra devolve o icone do
// painel lateral ao cabecalho. Rotulo vazio faz o Button virar quadrado
// sozinho (isIconOnly), sem gambiarra de CSS.
const { width: larguraJanela } = useWindowSize();
const soIcone = computed(() => larguraJanela.value < 768);
const rotulo = chave => (soIcone.value ? '' : t(chave));
const { checkMissingAttributes } = useConversationRequiredAttributes();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const isBotToggling = ref(false);
const resolveAttributesModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(
  () => store.getters['unifiedView/getEffectiveChat']
);

const inbox = computed(() => {
  const inboxId = currentChat.value.inbox_id;
  return inboxId ? store.getters['inboxes/getInbox'](inboxId) : {};
});

const hasBotConfigured = computed(() => !!inbox.value.active_bot);
const isBotActive = computed(() => !!currentChat.value.bot_active);

// Captain assistant info (Camada 1)
const captainAssistant = computed(() => inbox.value.captain_assistant || null);
const assistantStatus = computed(() => captainAssistant.value?.status || null);
const assistantName = computed(() => captainAssistant.value?.name || '');

// Camada 1 — assistant pode responder nesta conversa?
const assistantCanRespondHere = computed(() => {
  if (!captainAssistant.value) return true; // sem captain, mantém comportamento de outros bots
  if (assistantStatus.value === 'active') return true;
  if (assistantStatus.value === 'testing') {
    const testIds = captainAssistant.value.test_conversation_ids || [];
    return testIds.includes(currentChat.value.id);
  }
  return false; // draft, paused
});

// ── Orquestracao: entrada orquestradora ganha 3 estados (🟢🟡⚪) + menu ──
const isOrchestratedEntry = computed(
  () => captainAssistant.value?.role === 'orchestrator'
);
const orchestratorReturns = computed(
  () => captainAssistant.value?.return_on_resolve === true
);
const botDisabledForever = computed(
  () => !!currentChat.value.additional_attributes?.captain_bot_disabled
);

// Estado consolidado pra renderização do botão
const botUiState = computed(() => {
  if (!hasBotConfigured.value) return 'no_bot';

  if (assistantStatus.value === 'draft') return 'assistant_draft';
  if (assistantStatus.value === 'paused') return 'assistant_paused_global';

  if (assistantStatus.value === 'testing') {
    if (!assistantCanRespondHere.value) return 'testing_excluded';
    return isBotActive.value ? 'testing_active' : 'testing_paused_here';
  }

  // entrada ORQUESTRADORA: 🟢 ativo / 🟡 em espera (rearma ao resolver) / ⚪ desligado
  if (isOrchestratedEntry.value) {
    if (isBotActive.value) return 'orch_on';
    if (botDisabledForever.value || !orchestratorReturns.value)
      return 'orch_disabled';
    return 'orch_standby';
  }

  // status === 'active' ou null (bot tradicional)
  return isBotActive.value ? 'active_on' : 'active_paused_here';
});

// Direção A — switch inline + rótulo. Cada estado define rótulo, tooltip, cores
// (Tailwind n-*), se mostra o switch e se está bloqueado (não clicável).
const BOT_STATE_STYLES = {
  active_on: {
    label: 'LABEL_BOT',
    tooltip: 'TOOLTIP_ON',
    pill: 'bg-n-teal-3 border-n-teal-6 text-n-teal-11',
    showSwitch: true,
    on: true,
    switchColor: 'bg-n-teal-9',
  },
  active_paused_here: {
    label: 'LABEL_BOT',
    tooltip: 'TOOLTIP_PAUSED',
    pill: 'bg-n-amber-3 border-n-amber-6 text-n-amber-11',
    showSwitch: true,
    on: false,
    switchColor: 'bg-n-amber-9',
  },
  testing_active: {
    label: 'LABEL_TEST',
    tooltip: 'TOOLTIP_TEST_ON',
    pill: 'bg-n-blue-3 border-n-blue-6 text-n-blue-11',
    showSwitch: true,
    on: true,
    switchColor: 'bg-n-blue-9',
  },
  testing_paused_here: {
    label: 'LABEL_TEST',
    tooltip: 'TOOLTIP_TEST_PAUSED',
    pill: 'bg-n-amber-3 border-n-amber-6 text-n-amber-11',
    showSwitch: true,
    on: false,
    switchColor: 'bg-n-amber-9',
  },
  assistant_draft: {
    label: 'LABEL_DRAFT',
    tooltip: 'TOOLTIP_DRAFT',
    pill: 'bg-n-slate-3 border-n-slate-5 text-n-slate-10',
    showSwitch: false,
    locked: true,
  },
  assistant_paused_global: {
    label: 'LABEL_PAUSED',
    tooltip: 'TOOLTIP_PAUSED_GLOBAL',
    pill: 'bg-n-slate-3 border-n-slate-5 text-n-slate-10',
    showSwitch: false,
    locked: true,
  },
  orch_on: {
    label: 'LABEL_BOT',
    tooltip: 'TOOLTIP_ORCH_ON',
    pill: 'bg-n-teal-3 border-n-teal-6 text-n-teal-11',
    menu: true,
  },
  orch_standby: {
    label: 'LABEL_STANDBY',
    tooltip: 'TOOLTIP_ORCH_STANDBY',
    pill: 'bg-n-amber-3 border-n-amber-6 text-n-amber-11',
    menu: true,
  },
  orch_disabled: {
    label: 'LABEL_OFF',
    tooltip: 'TOOLTIP_ORCH_DISABLED',
    pill: 'bg-n-slate-3 border-n-slate-5 text-n-slate-10',
    menu: true,
  },
  testing_excluded: {
    label: 'LABEL_BOT',
    tooltip: 'TOOLTIP_TEST_EXCLUDED',
    pill: 'bg-n-slate-3 border-n-slate-5 text-n-slate-10',
    showSwitch: false,
    locked: true,
  },
};

const botStyle = computed(() => BOT_STATE_STYLES[botUiState.value] || null);

const botTooltip = computed(() => {
  if (!botStyle.value) return t('CONVERSATION.BOT_TOGGLE.NO_BOT');
  return t(`CONVERSATION.BOT_TOGGLE.${botStyle.value.tooltip}`, {
    name: assistantName.value,
  });
});

// quem esta COM A BOLA: ultimo agent_name gravado nas mensagens do bot
// ('clara_-_comercial' -> 'Clara - Comercial'); cadeia porta › agente quando
// um especialista assumiu via orquestracao
const prettyAgentName = raw =>
  String(raw)
    .split('_')
    .map(part => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ')
    .trim();

const currentSpeaker = computed(() => {
  const messages = currentChat.value.messages || [];
  // "devolver a triagem" grava um marco de novo ciclo: falas de agente
  // ANTERIORES ao marco nao contam — a cadeia volta a mostrar so a porta
  const restartRaw =
    currentChat.value.additional_attributes?.captain_cycle_restart_at;
  const restartAt = restartRaw ? Date.parse(restartRaw) / 1000 : 0;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    const raw = message?.additional_attributes?.agent_name;
    if (raw && !message.private && (message.created_at || 0) > restartAt) {
      return prettyAgentName(raw);
    }
  }
  return null;
});

const normalized = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const botLabel = computed(() => {
  const state = botUiState.value;
  // orquestracao ATIVA: mostra a cadeia (porta › quem atende)
  if (state === 'orch_on') {
    const entry = assistantName.value;
    const speaker = currentSpeaker.value;
    if (speaker && normalized(speaker) !== normalized(entry)) {
      return `${entry} › ${speaker}`;
    }
    return entry;
  }
  // bot standalone ativo: nome do assistente em vez de "Bot" generico
  if (state === 'active_on' && assistantName.value) return assistantName.value;
  return t(
    `CONVERSATION.BOT_TOGGLE.${botStyle.value ? botStyle.value.label : 'LABEL_BOT'}`
  );
});

const botPillClass = computed(() => botStyle.value?.pill ?? '');
const botShowSwitch = computed(() => Boolean(botStyle.value?.showSwitch));
const botSwitchOn = computed(() => Boolean(botStyle.value?.on));
const botSwitchColor = computed(
  () => botStyle.value?.switchColor ?? 'bg-n-slate-6'
);
const botSwitchText = computed(() =>
  botSwitchOn.value
    ? t('CONVERSATION.BOT_TOGGLE.ON')
    : t('CONVERSATION.BOT_TOGGLE.OFF')
);
const botButtonDisabled = computed(() => Boolean(botStyle.value?.locked));

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

// menu de estados (entrada orquestradora)
const botHasMenu = computed(() => Boolean(botStyle.value?.menu));
const showBotMenu = ref(false);
const botMenuRef = ref(null);
onClickOutside(botMenuRef, () => {
  showBotMenu.value = false;
});

const botMenuActions = computed(() => {
  const state = botUiState.value;
  if (state === 'orch_on') {
    return ['assume', 'return_to_triage', 'disable'];
  }
  if (state === 'orch_standby') {
    return ['reactivate', 'return_to_triage', 'disable'];
  }
  return ['reactivate', 'return_to_triage'];
});

const runBotStateAction = async stateAction => {
  showBotMenu.value = false;
  isBotToggling.value = true;
  try {
    await store.dispatch('setBotState', {
      conversationId: currentChat.value.id,
      stateAction,
    });
    toast.success(
      t(`CONVERSATION.BOT_TOGGLE.STATE_DONE_${stateAction.toUpperCase()}`)
    );
  } catch {
    toast.error(t('CONVERSATION.BOT_TOGGLE.FAILED'));
  } finally {
    isBotToggling.value = false;
  }
};

const onBotPillClick = () => {
  if (botHasMenu.value) {
    showBotMenu.value = !showBotMenu.value;
    return;
  }
  toggleBotStatus(); // eslint-disable-line no-use-before-define
};

const toggleBotStatus = async () => {
  isBotToggling.value = true;
  try {
    await store.dispatch('toggleBot', {
      conversationId: currentChat.value.id,
    });
    const label = currentChat.value.bot_active
      ? t('CONVERSATION.BOT_TOGGLE.ACTIVATED')
      : t('CONVERSATION.BOT_TOGGLE.DEACTIVATED');
    toast.success(label);
  } catch {
    toast.error(t('CONVERSATION.BOT_TOGGLE.FAILED'));
  } finally {
    isBotToggling.value = false;
  }
};

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = async (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  try {
    await store.dispatch('toggleStatus', payload);
    toast.success(t('CONVERSATION.CHANGE_STATUS'));
  } catch {
    toast.error(t('CONVERSATION.CHANGE_STATUS_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

// No celular, Adiar e Pendente moram no menu ⋮ (MoreActions) e chamam daqui.
defineExpose({ openSnoozeModal, toggleStatus });

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex relative justify-end items-center gap-1.5 resolve-actions">
    <div
      v-if="hasBotConfigured"
      ref="botMenuRef"
      class="relative flex-shrink-0"
    >
      <button
        v-tooltip="botTooltip"
        type="button"
        class="flex items-center gap-1.5 px-2.5 max-md:px-1.5 py-1 rounded-lg border text-xs font-semibold transition-colors flex-shrink-0 disabled:cursor-not-allowed"
        :class="botPillClass"
        :disabled="botButtonDisabled || isBotToggling"
        @click="onBotPillClick"
      >
        <span
          class="grid size-4 flex-shrink-0 place-content-center rounded-full brain-face"
        >
          <span class="i-ph-sparkle-fill size-2.5 text-white" />
        </span>
        <span class="hidden md:inline">{{ botLabel }}</span>
        <span
          v-if="botShowSwitch"
          class="relative h-4 rounded-full transition-colors w-7 flex-shrink-0"
          :class="botSwitchOn ? botSwitchColor : 'bg-n-slate-6'"
        >
          <span
            class="absolute w-3 h-3 transition-transform bg-white rounded-full shadow-sm top-0.5 ltr:left-0.5 rtl:right-0.5"
            :class="
              botSwitchOn
                ? 'ltr:translate-x-3 rtl:-translate-x-3'
                : 'translate-x-0'
            "
          />
        </span>
        <span
          v-if="botShowSwitch"
          class="hidden md:inline text-[10px] font-medium opacity-70"
        >
          {{ botSwitchText }}
        </span>
        <span
          v-if="botHasMenu"
          class="hidden md:inline i-lucide-chevron-down size-3 opacity-70"
        />
      </button>

      <!-- Menu de 3 estados (entrada orquestradora) -->
      <div
        v-if="botHasMenu && showBotMenu"
        class="absolute top-8 ltr:right-0 rtl:left-0 z-40 w-56 py-1 rounded-xl bg-n-solid-1 border border-n-weak shadow-xl flex flex-col"
      >
        <button
          v-for="stateAction in botMenuActions"
          :key="stateAction"
          class="flex items-center gap-2 px-3 py-2 text-xs text-n-slate-12 hover:bg-n-alpha-1 cursor-pointer text-left"
          @click="runBotStateAction(stateAction)"
        >
          <span
            class="size-3.5 flex-shrink-0"
            :class="{
              'i-lucide-user': stateAction === 'assume',
              'i-lucide-power-off': stateAction === 'disable',
              'i-lucide-play': stateAction === 'reactivate',
              'i-lucide-shuffle': stateAction === 'return_to_triage',
            }"
          />
          {{
            $t(`CONVERSATION.BOT_TOGGLE.ACTION_${stateAction.toUpperCase()}`)
          }}
        </button>
      </div>
    </div>
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow-sm outline-1 outline outline-transparent"
    >
      <Button
        v-if="isOpen"
        v-tooltip="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        :label="rotulo('CONVERSATION.HEADER.RESOLVE_ACTION')"
        :title="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        icon="i-lucide-circle-check-big"
        size="xs"
        color="slate"
        variant="faded"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0 max-md:!rounded-lg"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        v-tooltip="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        :label="rotulo('CONVERSATION.HEADER.REOPEN_ACTION')"
        :title="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        icon="i-lucide-inbox"
        size="xs"
        color="amber"
        variant="faded"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0 max-md:!rounded-lg"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        v-tooltip="t('CONVERSATION.HEADER.OPEN_ACTION')"
        :label="rotulo('CONVERSATION.HEADER.OPEN_ACTION')"
        :title="t('CONVERSATION.HEADER.OPEN_ACTION')"
        icon="i-lucide-inbox"
        size="xs"
        color="blue"
        variant="faded"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="xs"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0 max-md:hidden"
        :color="isOpen ? 'slate' : isResolved ? 'amber' : 'blue'"
        :variant="isOpen ? 'faded' : 'faded'"
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
